import React, { useEffect, useState } from "react";
import { Lock, Award } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Progress } from "../components/ui/progress";
import StatusBadge from "../components/StatusBadge";
import { useToast } from "../hooks/use-toast";
import { useAuth } from "../context/AuthContext";
import api, { errMsg } from "../api";

function buildEmployment(s) {
  if (!s) return [];
  return [
    { label: "Current role", value: s.role },
    { label: "Current status", value: s.status },
    { label: "Assigned contact", value: s.manager_name ? `${s.manager_name} (${s.manager_role})` : "Not assigned yet" },
    { label: "Training progress", value: s.training_total ? `${s.training_done}/${s.training_total} modules` : "No modules published" },
    { label: "Assessment score", value: s.assessment_score != null ? `${s.assessment_score}%` : "Not taken" },
    { label: "Certification status", value: s.certified ? "Certified" : "Not certified" },
    { label: "Client program", value: s.program_name || "None" },
    { label: "Program training stage", value: s.workforce_stage + (s.training_scheduled_date ? ` · ${s.training_scheduled_date}` : "") },
    { label: "Equipment", value: s.equipment_status || "No request" },
    { label: "Availability", value: s.availability_status || "Not submitted" },
  ];
}

function buildAchievements(s, docs, completion) {
  if (!s) return [];
  return [
    { title: "Profile Completed", desc: "Master profile fully filled out.", earned: completion >= 100 },
    { title: "Documents Cleared", desc: "Every required document approved.", earned: docs.length > 0 && docs.every((d) => d.status === "Approved") },
    { title: "Training Graduate", desc: "Finished all core training modules.", earned: s.training_total > 0 && s.training_done >= s.training_total },
    { title: "Certified CSR", desc: "Earned the Customer Service Representative credential.", earned: s.certified },
    { title: "Client Ready", desc: "Approved for client assignment.", earned: s.client_ready },
    { title: "Top Performer", desc: "Scored 95% or higher on the final assessment.", earned: (s.assessment_score ?? 0) >= 95 },
  ];
}

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

export default function Profile() {
  const { toast } = useToast();
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", address: "", emergency_name: "", emergency_phone: "" });
  const [docs, setDocs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailMsg, setEmailMsg] = useState("");

  useEffect(() => {
    if (user) setForm({
      first_name: user.first_name || "", last_name: user.last_name || "",
      phone: user.phone || "", address: user.address || "",
      emergency_name: user.emergency_name || "", emergency_phone: user.emergency_phone || "",
    });
  }, [user]);

  useEffect(() => {
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
    api.get("/me/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      toast({ title: "Name required", description: "First and last name are required.", variant: "destructive" });
      return;
    }
    if (form.phone && form.phone.replace(/\D/g, "").length < 10) {
      toast({ title: "Invalid phone", description: "Enter a 10-digit phone number.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const res = await api.put("/profile", form);
      setUser(res.data);
      toast({ title: "Profile saved", description: "Your changes have been stored." });
    } catch (err) {
      toast({ title: "Could not save profile", description: errMsg(err), variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const initials = ((user?.first_name?.[0] || "") + (user?.last_name?.[0] || "")).toUpperCase();
  const completion = user?.profile_completion ?? 0;

  const changeEmail = async (e) => {
    e.preventDefault();
    setEmailMsg("");
    setEmailBusy(true);
    try {
      const r = await api.post("/auth/change-email", emailForm);
      setEmailMsg(r.data.message || "Confirmation email sent. Please check your new email address to complete the change.");
      setEmailForm({ new_email: "", current_password: "" });
      toast({ title: "Confirmation email sent", description: "Check your new inbox to finish the change." });
    } catch (err) {
      toast({ title: "Could not change email", description: err.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setEmailBusy(false);
    }
  };

  const progress = [
    { label: "Overall", value: user?.onboarding_percent ?? 0 },
    { label: "Profile", value: completion },
    { label: "Documents", value: docs.length ? Math.round(docs.filter((d) => d.status === "Approved").length / docs.length * 100) : 0 },
  ];

  const isClient = (user?.role || "").startsWith("client");
  return (
    <div className="space-y-6">
      <Card>
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-violet-100 text-xl font-bold text-violet-700">{initials}</div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">{user?.first_name} {user?.last_name}</h2>
            <div className="mt-1 flex items-center gap-2">
              <StatusBadge status="In progress" />
              <span className="text-sm text-slate-500">{user?.status}</span>
            </div>
            <div className="mt-1 text-sm text-slate-500">{user?.email}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs text-slate-400">Profile completion</div>
            <div className="text-2xl font-bold text-violet-600">{completion}%</div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <h2 className="text-base font-bold text-slate-900">Personal information</h2>
            <form onSubmit={save} className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label>First name</Label><Input value={form.first_name} onChange={set("first_name")} data-testid="profile-first-name" /></div>
              <div className="space-y-1.5"><Label>Last name</Label><Input value={form.last_name} onChange={set("last_name")} data-testid="profile-last-name" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input value={user?.email || ""} disabled /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="profile-phone" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Mailing address</Label><Input value={form.address} onChange={set("address")} data-testid="profile-address" /></div>
              <div className="space-y-1.5"><Label>Emergency contact name</Label><Input value={form.emergency_name} onChange={set("emergency_name")} placeholder="Contact name" data-testid="profile-emergency-name" /></div>
              <div className="space-y-1.5"><Label>Emergency contact phone</Label><Input value={form.emergency_phone} onChange={set("emergency_phone")} placeholder="(555) 000-0000" data-testid="profile-emergency-phone" /></div>
              <p className="text-xs text-slate-400 sm:col-span-2">Weekly availability is managed on the <a href="/availability" className="text-violet-600 hover:underline">Availability</a> page.</p>
              <div className="sm:col-span-2"><Button type="submit" disabled={saving} data-testid="profile-save-btn" className="btn-primary-gradient border-0 text-white">{saving ? "Saving…" : "Save changes"}</Button></div>
            </form>
          </Card>

          <Card>
            <h2 className="text-base font-bold text-slate-900">Account email</h2>
            <p className="text-sm text-slate-500">Your sign-in email address. Changing it requires confirmation at the new address.</p>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-100 p-3">
              <div>
                <div className="text-xs text-slate-400">Current email</div>
                <div className="text-sm font-medium text-slate-800" data-testid="current-email">{user?.email}</div>
              </div>
              <Button type="button" variant="outline" onClick={() => { setEmailOpen((v) => !v); setEmailMsg(""); }} data-testid="change-email-toggle">
                {emailOpen ? "Cancel" : "Change Email"}
              </Button>
            </div>
            {emailMsg && <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700" data-testid="email-change-message">{emailMsg}</div>}
            {emailOpen && (
              <form onSubmit={changeEmail} className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5 sm:col-span-2"><Label>New email address</Label><Input type="email" value={emailForm.new_email} onChange={(e) => setEmailForm({ ...emailForm, new_email: e.target.value })} data-testid="new-email-input" required /></div>
                <div className="space-y-1.5 sm:col-span-2"><Label>Confirm your current password</Label><Input type="password" value={emailForm.current_password} onChange={(e) => setEmailForm({ ...emailForm, current_password: e.target.value })} data-testid="email-current-password-input" required /></div>
                <div className="sm:col-span-2"><Button type="submit" disabled={emailBusy} data-testid="update-email-btn" className="btn-primary-gradient border-0 text-white">{emailBusy ? "Sending…" : "Update Email"}</Button></div>
              </form>
            )}
          </Card>

          <Card className={isClient ? "hidden" : ""}>
            <h2 className="text-base font-bold text-slate-900">Employment information</h2>
            <p className="text-sm text-slate-500">Read from your master profile — updated automatically as you progress.</p>
            <div className="mt-4 divide-y divide-slate-100" data-testid="employment-info">
              {buildEmployment(summary).map((e) => (
                <div key={e.label} className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-slate-500">{e.label}</span>
                  <span className="text-sm font-medium capitalize text-slate-800">{e.value}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card className={isClient ? "hidden" : ""}>
            <h2 className="text-base font-bold text-slate-900">Achievements</h2>
            <p className="text-sm text-slate-500">Badges unlock automatically from your verified progress.</p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {buildAchievements(summary, docs, completion).map((a) => (
                <div key={a.title} data-testid={`achievement-${a.earned ? "earned" : "locked"}`} className={`flex items-start gap-3 rounded-xl border p-3 ${a.earned ? "border-emerald-200 bg-emerald-50/50" : "border-slate-100"}`}>
                  <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${a.earned ? "bg-emerald-100" : "bg-slate-100"}`}>{a.earned ? <Award className="h-4 w-4 text-emerald-600" /> : <Lock className="h-4 w-4 text-slate-400" />}</div>
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{a.title}</div>
                    <div className="text-xs text-slate-500">{a.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className={isClient ? "hidden" : ""}>
            <h2 className="text-base font-bold text-slate-900">Documents on file</h2>
            <p className="text-sm text-slate-500">Shared with your onboarding team for review.</p>
            <div className="mt-4 space-y-2">
              {docs.slice(0, 4).map((d) => (
                <div key={d.id} className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">{d.name}</span>
                  <StatusBadge status={d.status} />
                </div>
              ))}
            </div>
          </Card>

          <Card className={isClient ? "hidden" : ""}>
            <h2 className="text-base font-bold text-slate-900">Progress</h2>
            <p className="text-sm text-slate-500">Calculated automatically from your profile.</p>
            <div className="mt-4 space-y-4">
              {progress.map((p) => (
                <div key={p.label}>
                  <div className="mb-1 flex items-center justify-between text-sm">
                    <span className="text-slate-600">{p.label}</span>
                    <span className="font-semibold text-slate-800">{p.value}%</span>
                  </div>
                  <Progress value={p.value} className="h-2" />
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
